import { getPaymentRedirectOrigin, MOSIDA_API_BASE_URL } from "../config/apiUrls";

export interface GenerateQrResponse {
    success: boolean;
    data?: {
        qrImageUrl?: string;
        /** Base upload URL sent to the QR API (before backend appends id). */
        uploadUrl?: string;
        /** Full URL phones open after scanning (includes id query param). */
        completeScanUrl?: string;
        [key: string]: any;
    };
    error?: string;
}

/** Build the mobile upload URL embedded in the image-upload QR code. */
export function buildImageUploadQrUrl(
    uuid: string,
    options?: { removeBg?: boolean }
): { uploadUrl: string; completeScanUrl: string } {
    // Base upload URL only. The generate-image-qr-code API already appends
    // `id` + `user_id` from the data payload — do not set `id` here or it duplicates.
    const upload = new URL(`${getPaymentRedirectOrigin()}/upload-image`);
    if (typeof options?.removeBg === "boolean") {
        upload.searchParams.set(
            "remove_bg",
            options.removeBg ? "true" : "false"
        );
    }
    const uploadUrl = upload.toString();

    // Mirror what the backend merges onto the scan URL for the phone.
    const complete = new URL(uploadUrl);
    complete.searchParams.set("id", uuid);
    return { uploadUrl, completeScanUrl: complete.toString() };
}

export async function generateImageQrCode(
    uuid: string,
    options?: { removeBg?: boolean }
): Promise<GenerateQrResponse> {
    const endpoint = `${MOSIDA_API_BASE_URL}/museums/generate-image-qr-code/${encodeURIComponent(uuid)}`;
    const { uploadUrl, completeScanUrl } = buildImageUploadQrUrl(uuid, options);
    try {
        const response = await fetch(endpoint, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                url: uploadUrl,
                // Backend merges `uuid` onto the scan URL as `id`.
                // Do not send userId — that becomes a redundant `user_id` query param.
                data: {
                    uuid,
                    ...(typeof options?.removeBg === "boolean"
                        ? { remove_bg: options.removeBg }
                        : {}),
                },
            }),
        });

        if (!response.ok) {
            const text = await response.text();
            return { success: false, error: `QR API failed: ${response.status} ${text}` };
        }
        const contentType = response.headers.get("content-type") || "";
        // If JSON, parse and extract. Otherwise, treat as plain text URL
        if (contentType.includes("application/json")) {
            const json = await response.json();
            const qrImageUrl = json?.data?.qr_image_url || json?.data?.qrImageUrl || json?.qr_image_url || json?.qrImageUrl || json?.data?.url || json?.url;
            return { success: true, data: { qrImageUrl, uploadUrl, completeScanUrl } };
        } else {
            const text = await response.text();
            const url = text?.trim();
            // Basic URL validation
            try {
                const u = new URL(url);
                return {
                    success: true,
                    data: { qrImageUrl: u.toString(), uploadUrl, completeScanUrl },
                };
            } catch {
                return { success: false, error: `Unexpected QR response: ${url?.slice(0, 100)}` };
            }
        }
    } catch (e: any) {
        return { success: false, error: e?.message || "Network error" };
    }
}
